<template>
  <div class="space-y-4">
    <div>
      <h3 class="text-base font-medium text-gray-900">7-Segment Display</h3>
      <p class="text-sm text-gray-500">Configure what to show on the display</p>
    </div>

    <!-- Display Mode Selector -->
    <div class="space-y-3">
      <label class="text-sm font-medium text-gray-700">Display Mode</label>
      <div class="flex space-x-2">
        <button
          v-for="mode in displayModes"
          :key="mode.value"
          @click="setDisplayMode(mode.value)"
          :disabled="!isOnline"
          :class="[
            'px-3 py-2 text-sm font-medium rounded-md',
            currentDisplayMode === mode.value
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
            !isOnline ? 'opacity-50 cursor-not-allowed' : ''
          ]"
        >
          {{ mode.icon }} {{ mode.label }}
        </button>
      </div>
    </div>

    <!-- Time Mode Configuration -->
    <div v-if="currentDisplayMode === 'time'" class="bg-blue-50 rounded-lg p-4">
      <div class="flex items-center space-x-2 mb-2">
        <span class="text-blue-600">🕐</span>
        <span class="text-sm font-medium text-blue-900">Current Time (Netherlands)</span>
      </div>
      <p class="text-xs text-blue-700">Shows current time in HH:MM:SS format</p>
    </div>

    <!-- Countup/Countdown Mode Configuration -->
    <div v-if="currentDisplayMode === 'countup' || currentDisplayMode === 'countdown'" 
         class="bg-pink-50 rounded-lg p-4 space-y-3">
      <div class="flex items-center space-x-2">
        <span class="text-pink-600">{{ currentDisplayMode === 'countup' ? '⏱️' : '⏰' }}</span>
        <span class="text-sm font-medium text-pink-900">
          {{ currentDisplayMode === 'countup' ? 'Count Up' : 'Count Down' }} Configuration
        </span>
      </div>

      <!-- Date Picker -->
      <div>
        <label class="block text-xs font-medium text-pink-800 mb-1">Special Date</label>
        <input
          type="date"
          v-model="localDisplayStartDate"
          @change="updateDisplayConfig"
          :disabled="!isOnline"
          class="block w-full px-3 py-2 text-sm border border-pink-200 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 disabled:opacity-50"
        >
      </div>

      <!-- Custom Message -->
      <div>
        <label class="block text-xs font-medium text-pink-800 mb-1">Custom Message</label>
        <input
          type="text"
          v-model="localDisplayMessage"
          @input="debounceDisplayMessage"
          :disabled="!isOnline"
          maxlength="18"
          placeholder="e.g., 'hours together', 'days since we met'"
          class="block w-full px-3 py-2 text-sm border border-pink-200 rounded-md focus:outline-none focus:ring-2 focus:ring-pink-500 disabled:opacity-50"
        >
      </div>

      <!-- Unit Selector -->
      <div>
        <label class="block text-xs font-medium text-pink-800 mb-1">Count in</label>
        <div class="flex space-x-2">
          <button
            v-for="unit in displayUnits"
            :key="unit.value"
            @click="setDisplayUnit(unit.value)"
            :disabled="!isOnline"
            :class="[
              'px-3 py-1 text-xs font-medium rounded',
              currentDisplayUnit === unit.value
                ? 'bg-pink-600 text-white'
                : 'bg-white text-pink-700 border border-pink-200 hover:bg-pink-50',
              !isOnline ? 'opacity-50 cursor-not-allowed' : ''
            ]"
          >
            {{ unit.label }}
          </button>
        </div>
      </div>

      <p class="text-xs text-pink-700">
        {{ currentDisplayMode === 'countup' 
            ? 'Shows time elapsed since the special date' 
            : 'Shows time remaining until the special date' }}
      </p>
    </div>

    <!-- Current Display State -->
    <div class="bg-gray-50 rounded-lg p-3">
      <div class="flex items-center justify-between text-sm">
        <span class="text-gray-600">Current Display:</span>
        <span class="font-medium">{{ displayCurrentState }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue'

const props = defineProps({
  device: {
    type: Object,
    required: true
  },
  isOnline: {
    type: Boolean,
    required: true
  }
})

const emit = defineEmits(['update-desired'])

// Local state for responsive UI
const localDisplayStartDate = ref('')
const localDisplayMessage = ref('')
let displayUpdateTimer = null

// Display mode configuration
const displayModes = [
  { value: 'time', label: 'Time', icon: '🕐' },
  { value: 'countup', label: 'Count Up', icon: '⏱️' },
  { value: 'countdown', label: 'Count Down', icon: '⏰' }
]

const displayUnits = [
  { value: 'days', label: 'Days' },
  { value: 'hours', label: 'Hours' },
  { value: 'minutes', label: 'Minutes' }
]

// Computed properties
const currentDisplayConfig = computed(() =>
  props.device.shadow?.desired?.displayConfig ||
  props.device.shadow?.reported?.displayConfig ||
  {}
)

const currentDisplayMode = computed(() =>
  currentDisplayConfig.value.mode || 'time'
)

const currentDisplayUnit = computed(() =>
  currentDisplayConfig.value.unit || 'days'
)

const currentDisplayStartDate = computed(() =>
  currentDisplayConfig.value.startDate || ''
)

const currentDisplayMessage = computed(() =>
  currentDisplayConfig.value.message || ''
)

const displayCurrentState = computed(() => {
  switch (currentDisplayMode.value) {
    case 'time':
      return '🕐 Current Time (NL)'
    case 'countup':
      return `⏱️ ${currentDisplayMessage.value || 'Time since'} ${currentDisplayStartDate.value || 'date'} (${currentDisplayUnit.value})`
    case 'countdown':
      return `⏰ ${currentDisplayMessage.value || 'Time until'} ${currentDisplayStartDate.value || 'date'} (${currentDisplayUnit.value})`
    default:
      return 'Unknown mode'
  }
})

// Watch for display configuration changes
watch(() => currentDisplayConfig.value.startDate, (newStartDate) => {
  if (newStartDate) {
    localDisplayStartDate.value = newStartDate
  }
}, { immediate: true })

watch(() => currentDisplayConfig.value.message, (newMessage) => {
  if (newMessage !== undefined) {
    localDisplayMessage.value = newMessage
  }
}, { immediate: true })

// Methods
const updateDesiredState = (newState) => {
  emit('update-desired', props.device.deviceId, newState)
}

const setDisplayMode = (mode) => {
  if (!props.isOnline) return
  updateDesiredState({ displayConfig: { ...currentDisplayConfig.value, mode } })
}

const setDisplayUnit = (unit) => {
  if (!props.isOnline) return
  updateDesiredState({ displayConfig: { ...currentDisplayConfig.value, unit } })
}

const updateDisplayConfig = () => {
  if (!props.isOnline) return
  updateDesiredState({
    displayConfig: {
      ...currentDisplayConfig.value,
      startDate: localDisplayStartDate.value,
      message: localDisplayMessage.value
    }
  })
}

const debounceDisplayMessage = () => {
  clearTimeout(displayUpdateTimer)
  displayUpdateTimer = setTimeout(() => {
    updateDesiredState({
      displayConfig: {
        ...currentDisplayConfig.value,
        message: localDisplayMessage.value
      }
    })
  }, 500) // 500ms debounce for text input
}
</script>